(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const reveal = () => {
    const items = document.querySelectorAll('[data-hsf-reveal]');
    if (!items.length) return;

    items.forEach((item) => item.classList.add('hsf-reveal'));

    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));
  };

  const addVariantToCart = async (button) => {
    const variantId = button.dataset.hsfAddVariant;
    if (!variantId || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });

      if (!response.ok) throw new Error('Cart add failed');
      const addedItem = await response.json();

      button.textContent = 'Added';
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart: null,
            giftOfferPlacement: addedItem.handle === 'hush-gummies' ? 'homepage_product_rail' : '',
            purchaseType: addedItem.selling_plan_allocation ? 'subscription' : 'one_time'
          }
        })
      );

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1400);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Try Again';
      window.setTimeout(() => {
        button.textContent = originalText;
      }, 1800);
    }
  };

  const bindCartButtons = () => {
    document.querySelectorAll('[data-hsf-add-variant]').forEach((button) => {
      if (button.dataset.hsfBound === 'true') return;
      button.dataset.hsfBound = 'true';
      button.addEventListener('click', () => addVariantToCart(button));
    });
  };

  const bindStickyBuy = () => {
    const sticky = document.querySelector('[data-hsf-sticky-buy]');
    const trigger = document.querySelector('[data-hsf-buy-trigger]');
    if (!sticky || !trigger) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(trigger);
  };

  const bindFormulaQuiz = () => {
    document.querySelectorAll('[data-hsf-quiz]').forEach((quiz) => {
      const result = quiz.querySelector('[data-hsf-quiz-result]');
      if (!result) return;

      quiz.querySelectorAll('[data-hsf-goal]').forEach((button) => {
        button.addEventListener('click', () => {
          const product = button.dataset.product || 'Shop Best Sellers';
          const link = button.dataset.link || '/collections/all';
          const bundle = button.dataset.bundle || 'Start with one formula, then build a ritual once you know what your body loves.';
          result.innerHTML = `<strong>${product}</strong><span>${bundle}</span><a class="hsf-btn" href="${link}">Shop Now</a>`;
        });
      });
    });
  };

  // 2026-07-18: subscription-first. The old preferOneTimePurchase() force-selected
  // the one-time radio and stripped selling_plan on every product pageview, which
  // silently zeroed out all subscription conversions. Inverted to prefer subscribe.
  const preferSubscription = () => {
    // 2026-07-18 subscription-first: on product pages, default the purchase mode to
    // Subscribe unless the shopper explicitly chose one-time. Never strips selling_plan.
    if (!document.querySelector('#MainContent[data-template^="product"], .product-information')) return;
    // HUSH uses an explicit one-time default in its dedicated conversion flow.
    if (/^\/products\/hush-gummies(?:\/|$)/.test(window.location.pathname)) return;
    if (new URLSearchParams(window.location.search).get('purchase') === 'one-time') return;

    let userChoseOneTime = false;
    document.addEventListener('change', (event) => {
      const t = event.target;
      if (t && t.matches && t.matches('.mrk-bundle__sub-radio')) {
        const oneTime = t.matches('[value="one_time"]') || t.dataset?.isSubscription === 'false';
        if (oneTime) userChoseOneTime = true;
      }
    }, true);

    const subRadio = () =>
      document.querySelector('.mrk-bundle__sub-radio[data-is-subscription="true"]') ||
      document.querySelector('.mrk-bundle__sub-radio[value="subscription"]');

    const chooseSubscribe = () => {
      if (userChoseOneTime) return true;
      const sub = subRadio();
      if (!sub) return false;
      if (!sub.checked) {
        sub.click();
        sub.checked = true;
        sub.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return sub.checked;
    };

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (chooseSubscribe() || attempts > 20) window.clearInterval(timer);
    }, 250);
    chooseSubscribe();
  };

  const syncProductIntroCopy = () => {
    const handle = window.location.pathname.split('/products/')[1]?.split('/')[0];
    if (!handle || handle === 'hush-gummies') return;

    const copyByHandle = {
      'hush-gummies': 'Hush is an evening gummy formula with 20 gummies and 20 servings per tin, one gummy per serving, and 0.35 mg melatonin per gummy.',
      bluecrush: 'Marine collagen peptides with hyaluronic acid, tremella, blue spirulina, amla, and sea moss for a simple daily beauty ritual.',
      shilajitgummy: 'Himalayan shilajit gummies for mineral-focused daily drive, routine momentum, and a cleaner format than resin.',
      eternity: 'A mushroom and botanical gummy with six mushroom extracts, beta-glucans, ginseng, maca, and ginkgo in a simple daily format.',
      cozy: 'Adaptogenic chai with mushrooms, ashwagandha, maca, shatavari, and warming spices for a caffeine-free comfort ritual.',
      shilajittablets: 'Himalayan shilajit tablets for trace-mineral replenishment, daily stamina support, and a simple high-potency routine.'
    };

    const copy = copyByHandle[handle];
    if (!copy) return;

    const knownIntroSnippets = [
      'Marine collagen beauty support',
      'Calm your evening routine',
      'Mineral-rich shilajit',
      '12-in-1 mushroom',
      'caffeine-free adaptogenic chai',
      'Himalayan shilajit tablets',
      'formula details',
      'lab reports',
      'mobile decision',
      'organized below'
    ];

    const target = Array.from(document.querySelectorAll('.product-details p, .group-block p')).find((paragraph) => {
      if (paragraph.closest('.mrk-bundle-wrap, .shopify-payment-button, .accelerated-checkout-block')) return false;
      const text = paragraph.textContent || '';
      return text.length > 45 && knownIntroSnippets.some((snippet) => text.includes(snippet));
    });

    if (target) target.textContent = copy;
  };

  const normalizeLegacyContent = () => {
    const assetMap = new Map([
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/articles/blog-adaptogen-hero.png?v=1774480500',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-eternity-staged.jpg?v=1772938316'
      ],
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-adaptogen-hero.png?v=1772929761',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-eternity-staged.jpg?v=1772938316'
      ],
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/Gemini_Generated_Image_ycf194ycf194ycf1_1.png',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-deva-staged.jpg?v=1772938299'
      ],
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/Gemini_Generated_Image_d1ihivd1ihivd1ih_1.png',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-eternity-staged.jpg?v=1772938316'
      ],
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/Gemini_Generated_Image_myg27amyg27amyg2_2.jpg',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-hush-staged.jpg?v=1772938270'
      ],
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-bc-staged.jpg',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/blog-blue-crush-staged.jpg?v=1772938250'
      ],
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/banner-our-story.jpg',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/founder-vanilla-orchids.jpg?v=1779255111'
      ],
      [
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/christian-portrait.jpg',
        'https://cdn.shopify.com/s/files/1/0699/5751/7532/files/founder-herbal-foraging.jpg?v=1779255112'
      ]
    ]);

    const swapValue = (value) => {
      if (!value) return value;
      let next = value;
      assetMap.forEach((replacement, original) => {
        next = next.split(original).join(replacement);
      });
      next = next.split('/blogs/hermetica/').join('/blogs/news/');
      next = next.split('aeo-block').join('quick-answer-block');
      next = next.split('aeo-label').join('quick-answer-label');
      return next;
    };

    document.querySelectorAll('img, source, video').forEach((node) => {
      ['src', 'srcset', 'poster', 'data-src', 'data-srcset'].forEach((attribute) => {
        const current = node.getAttribute(attribute);
        const next = swapValue(current);
        if (next && next !== current) node.setAttribute(attribute, next);
      });
    });

    document.querySelectorAll('[href]').forEach((node) => {
      const current = node.getAttribute('href');
      const next = swapValue(current);
      if (next && next !== current) node.setAttribute('href', next);
    });

    document.querySelectorAll('[style]').forEach((node) => {
      const current = node.getAttribute('style');
      const next = swapValue(current);
      if (next && next !== current) node.setAttribute('style', next);
    });

    document.querySelectorAll('[class*="aeo-"]').forEach((node) => {
      const current = node.getAttribute('class');
      const next = swapValue(current);
      if (next && next !== current) node.setAttribute('class', next);
    });

    document.querySelectorAll('style').forEach((node) => {
      const current = node.textContent;
      const next = swapValue(current);
      if (next && next !== current) node.textContent = next;
    });

    const main = document.querySelector('#MainContent');
    if (!main) return;

    const h1s = Array.from(main.querySelectorAll('h1')).filter((heading) => {
      const rect = heading.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    const seen = new Set();
    h1s.forEach((heading) => {
      const key = heading.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      if (!key || !seen.has(key)) {
        seen.add(key);
        return;
      }
      const replacement = document.createElement('h2');
      replacement.className = heading.className;
      replacement.innerHTML = heading.innerHTML;
      heading.replaceWith(replacement);
    });
  };

  const injectProductTrustBadges = () => {
    const handle = window.location.pathname.split('/products/')[1]?.split('/')[0];
    if (handle === 'hush-gummies') return;

    const productMain = document.querySelector('#MainContent[data-template^="product"], .product-information');
    if (!productMain || document.querySelector('.hsf-top-trust-badges')) return;

    const anchor =
      document.querySelector('#MainContent[data-template^="product"] .ptags-wrap') ||
      document.querySelector('#MainContent[data-template^="product"] .ptags-list') ||
      document.querySelector('.product-details .ptags-wrap') ||
      document.querySelector('.product-details .ptags-list');

    if (!anchor) return;

    const badges = [
      ['★', '1,000+ Reviews'],
      ['3P', 'Third-Party Tested'],
      ['HM', 'Heavy-Metal Screened'],
      ['cGMP', 'cGMP Facilities']
    ];

    const row = document.createElement('div');
    row.className = 'hsf-top-trust-badges';
    row.setAttribute('aria-label', 'Hermetica trust badges');
    row.innerHTML = badges
      .map(([mark, label]) => {
        const tag = mark === 'TP' ? 'a' : 'span';
        const href = mark === 'TP' ? ' href="https://www.trustpilot.com/review/hermeticasuperfoods.com" target="_blank" rel="noopener"' : '';
        return `<${tag} class="hsf-top-trust-badge" data-badge="${mark}"${href}><strong>${mark}</strong><small>${label}</small></${tag}>`;
      })
      .join('');

    anchor.insertAdjacentElement('afterend', row);
  };

  const bindOpalPointer = () => {
    if (reducedMotion || window.matchMedia('(hover: none)').matches) return;

    document.querySelectorAll('.hsf-glass, .hsf-editorial-card, .hsf-expert-card, .product-detail-padd').forEach((card) => {
      if (card.dataset.hsfOpalBound === 'true') return;
      card.dataset.hsfOpalBound = 'true';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--hsf-mx', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        card.style.setProperty('--hsf-my', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      });
    });
  };

  const dismissMobilePromoOverlays = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const hideNode = (node) => {
      if (!node) return;
      node.style.setProperty('display', 'none', 'important');
      node.style.setProperty('visibility', 'hidden', 'important');
      node.style.setProperty('pointer-events', 'none', 'important');
    };

    const hideTextOverlay = (node) => {
      let current = node.closest('.needsclick, .klaviyo-form, [class*="kl-private-reset-css"]');
      if (!current) return;

      for (let depth = 0; current && depth < 8; depth += 1) {
        if (
          current === document.body ||
          current === document.documentElement ||
          current.matches('#MainContent, main, header, footer, .content-for-layout')
        ) {
          break;
        }

        const styles = window.getComputedStyle(current);
        const rect = current.getBoundingClientRect();
        if (styles.position === 'fixed' || (rect.width >= window.innerWidth * 0.7 && rect.height > 120)) {
          hideNode(current);
          return;
        }
        current = current.parentElement;
      }

      hideNode(node);
    };

    let attempts = 0;
    const scan = () => {
      document.querySelectorAll('.needsclick, .klaviyo-form, [class*="kl-private-reset-css"]').forEach((node) => {
        const text = (node.innerText || '').replace(/\s+/g, ' ').trim();
        if (/Unlock 10% off|Join The Team|NO, THANKS/i.test(text)) {
          hideTextOverlay(node);
        }
      });
    };

    scan();
    const timer = window.setInterval(() => {
      attempts += 1;
      scan();
      if (attempts > 24) window.clearInterval(timer);
    }, 350);
  };

  const simplifyCinemaReel = () => {
    const reel = document.querySelector('.hsf-cinema-reel');
    if (!reel) return;

    const products = {
      '/products/hush-gummies': { title: 'Hush Sleep Gummies', badge: 'Sleep', price: 'From $40' },
      '/products/shilajittablets': { title: 'Meru Shilajit Tablets', badge: 'Shilajit', price: 'From $55' },
      '/products/bluecrush': { title: 'Blue Crush Collagen', badge: 'Collagen', price: 'From $60' },
      '/products/shilajitgummy': { title: 'Deva Shilajit Gummies', badge: 'Shilajit', price: 'From $60' },
      '/products/eternity': { title: 'Eternity Mushroom Gummies', badge: 'Mushrooms', price: 'From $50' },
      '/products/cozy': { title: 'Cozy Adaptogenic Chai', badge: 'Chai', price: 'From $45' }
    };

    const renderProductTag = (media, product) => {
      if (!media) return;
      media.querySelectorAll(':scope > span:not(.hsf-cinema-card__tag-kicker)').forEach((node) => node.remove());

      let tag = media.querySelector('.hsf-cinema-card__tag');
      if (!tag) {
        tag = document.createElement('div');
        tag.className = 'hsf-cinema-card__tag';
        tag.append(document.createElement('span'), document.createElement('strong'), document.createElement('small'));
        media.appendChild(tag);
      }

      let kicker = tag.querySelector('span');
      let title = tag.querySelector('strong');
      let price = tag.querySelector('small');
      if (!kicker) {
        kicker = document.createElement('span');
        tag.appendChild(kicker);
      }
      if (!title) {
        title = document.createElement('strong');
        tag.appendChild(title);
      }
      if (!price) {
        price = document.createElement('small');
        tag.appendChild(price);
      }

      kicker.textContent = product.badge;
      title.textContent = product.title;
      price.textContent = product.price;
    };

    const renderIdentity = (body, product) => {
      if (!body) return;
      let identity = body.querySelector('.hsf-cinema-card__identity');
      let title = body.querySelector('h3');
      if (!identity) {
        identity = document.createElement('div');
        identity.className = 'hsf-cinema-card__identity';
        if (title) {
          title.replaceWith(identity);
          identity.appendChild(title);
        } else {
          title = document.createElement('h3');
          identity.appendChild(title);
          body.prepend(identity);
        }
      }

      let price = identity.querySelector('.hsf-cinema-card__price');
      if (!price) {
        price = document.createElement('p');
        price.className = 'hsf-cinema-card__price';
        identity.appendChild(price);
      }

      title.textContent = product.title;
      price.textContent = product.price;
    };

    const eyebrow = reel.querySelector('.hsf-cinema-reel__copy .hsf-eyebrow');
    const heading = reel.querySelector('.hsf-cinema-reel__copy h2');
    if (eyebrow) eyebrow.textContent = 'Product Films';
    if (heading) heading.textContent = 'Watch the formulas.';

    reel.querySelectorAll('.hsf-cinema-reel__copy p:not(.hsf-eyebrow), .hsf-cinema-reel__links, .hsf-cinema-card__body p').forEach((node) => node.remove());

    reel.querySelectorAll('.hsf-cinema-card').forEach((card) => {
      const mediaLink = card.querySelector('.hsf-cinema-card__media[href]');
      const href = mediaLink?.getAttribute('href') || '/collections/all';
      const path = href.startsWith('http') ? new URL(href).pathname : href;
      const product = products[path] || { title: 'Shop Formula', badge: 'Formula' };
      const body = card.querySelector('.hsf-cinema-card__body');

      renderProductTag(mediaLink, product);
      renderIdentity(body, product);
      if (mediaLink) mediaLink.setAttribute('aria-label', `Shop ${product.title}`);
      if (!body || body.querySelector('.hsf-cinema-card__cta')) return;

      const cta = document.createElement('a');
      cta.className = 'hsf-cinema-card__cta';
      cta.href = href;
      cta.textContent = `Shop ${(product.title || 'Formula').replace(/^Shop\s+/i, '')}`;
      body.appendChild(cta);
    });
  };

  const respectReducedMotion = () => {
    if (!reducedMotion) return;

    document.querySelectorAll('.hsf-arrival__video, video[autoplay]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
    });
  };

  const init = () => {
    respectReducedMotion();
    normalizeLegacyContent();
    reveal();
    bindCartButtons();
    bindStickyBuy();
    bindFormulaQuiz();
    preferSubscription();
    syncProductIntroCopy();
    injectProductTrustBadges();
    bindOpalPointer();
    dismissMobilePromoOverlays();
    simplifyCinemaReel();
    window.setTimeout(normalizeLegacyContent, 600);
    window.setTimeout(simplifyCinemaReel, 600);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* HSF bottom products stale-cache emergency 2026-06-18 */
(function(){
  if (window.__hsfBottomProductsStaleCacheFix) return;
  window.__hsfBottomProductsStaleCacheFix = true;
  var css = 'body .hsf-link-network{position:relative!important;z-index:30!important;isolation:isolate!important;display:block!important;visibility:visible!important;opacity:1!important;width:100%!important;min-height:0!important;margin:0!important;transform:none!important;background-color:#f8f7f3!important;background-image:none!important;pointer-events:auto!important;}' +
    'body .hsf-link-network__inner,body .hsf-link-network__grid,body .hsf-link-network__item{position:relative!important;z-index:1!important;visibility:visible!important;opacity:1!important;}';
  function inject(){
    if (!document.getElementById('hsf-bottom-products-stale-cache-css')) {
      var style = document.createElement('style');
      style.id = 'hsf-bottom-products-stale-cache-css';
      style.textContent = css;
      (document.head || document.documentElement).appendChild(style);
    }
  }
  inject();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', inject, { once: true });
  window.addEventListener('pageshow', inject);
  setTimeout(inject, 500);
  setTimeout(inject, 2000);
})();


/* Cart line one-tap subscription upgrade (2026-08-11). */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-hsf-sub-upgrade]');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = 'Upgrading…';
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: btn.getAttribute('data-line-key'),
        quantity: parseInt(btn.getAttribute('data-qty'), 10) || 1,
        selling_plan: parseInt(btn.getAttribute('data-plan'), 10),
      }),
    })
      .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then(function () { window.location.reload(); })
      .catch(function () { btn.disabled = false; btn.textContent = 'Subscribe & save 25% →'; });
  });
})();

/* Paid items before gift rows in every cart render (2026-08-11). CSS ordering
   lost a cascade war with the drawer's table styles, so the order is enforced
   in the DOM: gift rows (.hsf-cart-offer-gift) move after paid rows whenever
   cart contents render or morph. */
(function () {
  function reorder(tbody) {
    var gifts = tbody.querySelectorAll('tr.hsf-cart-offer-gift');
    if (!gifts.length) return;
    var last = tbody.lastElementChild;
    var needsMove = false;
    var rows = tbody.querySelectorAll('tr.cart-items__table-row');
    var seenGift = false;
    rows.forEach(function (r) {
      if (r.classList.contains('hsf-cart-offer-gift')) seenGift = true;
      else if (seenGift) needsMove = true;
    });
    if (!needsMove) return;
    gifts.forEach(function (g) { tbody.appendChild(g); });
  }
  function scan() {
    document.querySelectorAll('.cart-items tbody').forEach(reorder);
  }
  var scheduled = false;
  var mo = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; scan(); });
  });
  function boot() {
    scan();
    mo.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
